import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import {
  Loader2, ChevronRight, Star, TrendingDown, SlidersHorizontal, Search,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import { api, type CanalResena, type FichaListing, type ResumenResenas } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import { useChartTheme, monthLabel } from '@/components/aperturas/shared';
import {
  fmt, Chip, Delta, Estrellas, NotaGrande, BarraReparto, BadgesListing, EtiquetaCanal,
  ETIQUETA_CANAL,
} from '@/components/reviews/shared';
import { classNames } from '@/lib/utils';
import type { CtxResenas } from './ReviewsLayout';

/** Los alojamientos cuyo nombre no trae prefijo de zona se agrupan aquí. */
const SIN_ZONA = 'Sin zona';

/** Canales que se ofrecen como filtro. Vrbo entra dentro de «Otras». */
const CANALES_FILTRO: CanalResena[] = ['airbnb', 'booking', 'otras'];

/** Sin acentos y en minúsculas: se busca «Cañón» escribiendo «canon». */
function normaliza(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

/** «1 reseña» / «34 reseñas»: el número y su sustantivo concordados. */
function plural(n: number, uno: string, varios: string): string {
  return `${n.toLocaleString('es-ES')} ${n === 1 ? uno : varios}`;
}

/**
 * Si la ventana llega a `minimo` px de ancho.
 *
 * Hace falta en JavaScript y no en CSS porque lo que decide no es cómo se ve el
 * gráfico, sino si se monta: `hidden lg:block` construiría igualmente el SVG de
 * recharts en el móvil, con su `ResponsiveContainer` y sus observadores de
 * tamaño, para no enseñarlo.
 */
function useEsAncha(minimo: number): boolean {
  const [ancha, setAncha] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(min-width: ${minimo}px)`).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${minimo}px)`);
    const alCambiar = (e: MediaQueryListEvent) => setAncha(e.matches);
    mq.addEventListener('change', alCambiar);
    return () => mq.removeEventListener('change', alCambiar);
  }, [minimo]);
  return ancha;
}

// ─── Piezas de la pantalla ──────────────────────────────────────────────────

/**
 * La franja de arriba, enlace entero al plan.
 *
 * Es un `<Link>` que ocupa toda la caja y no un botón dentro de una tarjeta: en
 * un móvil el pulgar apunta al bloque de color, no a las cuatro palabras
 * subrayadas que hay dentro.
 */
function FranjaAlerta({ alerta }: { alerta: ResumenResenas['alerta'] }) {
  const critico = alerta.criticos > 0;
  const abierto = alerta.abiertos > 0;

  // El ámbar se resuelve solo con la tabla de `index.css`; el rosa y el verde no
  // tienen override para `border-*-200` ni para `text-*-700`, así que su `dark:`
  // va escrito aquí o en modo oscuro quedan borde claro y texto ilegible.
  const tono = critico
    ? 'bg-rose-50 border-rose-200 text-rose-700 dark:border-rose-900/50 dark:text-rose-300'
    : abierto
      ? 'bg-amber-50 border-amber-200 text-amber-700'
      : 'bg-emerald-50 border-emerald-200 text-emerald-700 dark:border-emerald-900/50 dark:text-emerald-300';

  const titulo = critico
    ? `${plural(alerta.criticos, 'tema crítico', 'temas críticos')} en ${plural(alerta.pisosCriticos, 'alojamiento', 'alojamientos')}`
    : abierto
      ? `${plural(alerta.abiertos, 'punto abierto', 'puntos abiertos')} en el plan de mejora`
      : 'Ningún punto abierto en el plan';

  const detalle = critico
    ? `${plural(alerta.abiertos, 'punto abierto', 'puntos abiertos')} · toca para abrir el plan`
    : abierto
      ? 'Toca para abrir el plan'
      : null;

  return (
    <Link
      to="/reviews/plan"
      className={classNames(
        'flex items-center gap-2 rounded-xl p-3 min-h-[60px] border',
        tono,
      )}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold">{titulo}</span>
        {detalle && <span className="block text-xs opacity-80 mt-0.5">{detalle}</span>}
      </span>
      {(critico || abierto) && <ChevronRight size={18} className="shrink-0" aria-hidden="true" />}
    </Link>
  );
}

/** Tile de KPI, mismo marcado que los de Huéspedes. */
function Tile({ icon, label, value, hint, tone }: {
  icon: ReactNode; label: string; value: ReactNode; hint?: ReactNode;
  tone?: 'default' | 'good' | 'warn';
}) {
  return (
    <div className="card p-3">
      <div className="flex items-center gap-1.5 text-xs font-medium text-sereno-dark/50">
        {icon}{label}
      </div>
      <div className={classNames(
        'text-xl font-bold mt-1 tabular-nums',
        tone === 'good' && 'text-emerald-600',
        tone === 'warn' && 'text-amber-600',
      )}>
        {value}
      </div>
      {hint && <div className="text-[11px] text-sereno-dark/40 mt-0.5 leading-snug">{hint}</div>}
    </div>
  );
}

/**
 * Un alojamiento del ranking.
 *
 * La tarjeta entera es el enlace y dentro no hay ni un solo botón: un control
 * accionable dentro de un `<a>` obliga a acertar en 40 px para hacer una cosa y
 * fallar para hacer la otra, y con el dedo no se acierta.
 *
 * Se enseña `nota5`, la media real; `score` —la bayesiana— solo ordena. Un piso
 * con dos cincos no puede encabezar la lista de los buenos, pero tampoco puede
 * aparecer con un 4,73 que nadie sabría de dónde sale.
 */
function TarjetaListing({ f }: { f: FichaListing }) {
  const canales = f.porCanal.filter(c => c.n > 0);
  const aRevisar = Math.max(0, f.temasAbiertos - f.criticos);

  return (
    <Link
      to={`/reviews/l/${f.listingId}`}
      className="block card p-3 min-h-[88px] transition-colors hover:border-slate-300 dark:hover:border-white/20"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1 flex flex-wrap items-center gap-x-1.5 gap-y-1">
          <span className="min-w-0 truncate text-[15px] font-semibold leading-tight">{f.nombre}</span>
          <BadgesListing badges={f.badges} />
        </div>
        <div className="shrink-0 flex flex-col items-end leading-tight">
          <NotaGrande nota={f.nota5} escala="xl" atenuada={f.badges.includes('pocas')} />
          <Delta valor={f.delta} />
        </div>
      </div>

      <div className="mt-1 text-xs text-slate-500 truncate">
        {plural(f.nResenas, 'reseña', 'reseñas')}
        {canales.map(c => (
          <span key={c.canal}>
            {' · '}
            <EtiquetaCanal canal={c.canal} nota5={c.nota5} nativa={c.nativa} escala={c.escala} />
          </span>
        ))}
      </div>

      <div className="mt-1.5 flex items-center justify-between gap-2">
        <BarraReparto reparto={f.reparto} />
        {f.peorSubnota && (
          <span className="text-[11px] text-slate-500 truncate">
            peor: {f.peorSubnota.etiqueta} {fmt(f.peorSubnota.sobre5, 1)}
          </span>
        )}
      </div>

      {f.temasAbiertos > 0 && (
        <div className="mt-1.5">
          {/* El ámbar ya lo apaga `index.css`; el rosa no está en esa tabla, así
              que lleva su `dark:` a mano para no quedar rosa chicle sobre negro. */}
          <span className={classNames(
            'badge text-[11px]',
            f.criticos > 0
              ? 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200'
              : 'bg-amber-100 text-amber-800',
          )}>
            {f.criticos > 0
              ? [
                  plural(f.criticos, 'crítico', 'críticos'),
                  aRevisar > 0 ? `${aRevisar.toLocaleString('es-ES')} a revisar` : null,
                ].filter(Boolean).join(' · ')
              : `${plural(f.temasAbiertos, 'tema', 'temas')} a revisar`}
          </span>
        </div>
      )}
    </Link>
  );
}

/**
 * Evolución mensual de la nota de toda la cartera.
 *
 * Solo se monta a partir de 1024 px. Es el único gráfico de la pantalla: en el
 * móvil la misma pregunta la contesta el `delta` de cada tarjeta, que además
 * dice de qué piso se trata.
 */
function GraficoCartera({ serie }: { serie: ResumenResenas['global']['serie'] }) {
  const chart = useChartTheme();
  const puntos = serie.filter(p => p.nota5 !== null);
  // Con un solo mes no hay línea que dibujar, solo un punto suelto que engaña.
  if (puntos.length < 2) return null;

  return (
    <div className="card p-4">
      <h2 className="font-semibold text-sereno-dark mb-2">Nota media mensual de la cartera</h2>
      <ResponsiveContainer width="100%" height={180}>
        <LineChart data={puntos} margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} />
          <XAxis dataKey="mes" tickFormatter={monthLabel} tick={{ fontSize: 11, fill: chart.axis }} />
          {/* El eje no arranca en 0: entre un 4,3 y un 4,8 se juega toda la
              sección, y con el 0 dentro las dos son la misma raya plana. */}
          <YAxis
            domain={['dataMin - 0.15', 5]}
            tickFormatter={(v: number) => fmt(v, 1)}
            tick={{ fontSize: 11, fill: chart.axis }}
            width={34}
          />
          <Tooltip
            cursor={{ stroke: chart.grid }}
            labelFormatter={(m: any) => monthLabel(String(m))}
            formatter={(v: any) => [fmt(Number(v), 2), 'Nota media']}
          />
          <Line
            type="monotone"
            dataKey="nota5"
            name="Nota media"
            stroke={chart.bar}
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Pantalla ───────────────────────────────────────────────────────────────

export default function ReviewsRankingPage() {
  const { rango } = useOutletContext<CtxResenas>();
  const esLg = useEsAncha(1024);

  /**
   * El canal viaja al servidor y la zona se filtra aquí.
   *
   * No es una incoherencia: cambiar de canal cambia las notas —la media de
   * Booking no es la media global— y eso solo lo sabe recalcular el servidor.
   * Filtrar por zona, en cambio, es esconder filas de una lista que ya está
   * entera en memoria, y hacerlo con una petición metería medio segundo de
   * espera en un gesto que debe ser instantáneo.
   */
  const [canal, setCanal] = useState<CanalResena | 'todos'>('todos');
  const [zonasElegidas, setZonasElegidas] = useState<string[]>([]);
  const [busqueda, setBusqueda] = useState('');
  const [panelAbierto, setPanelAbierto] = useState(false);

  const { data, loading, error } = useApi(
    () => api.getResumenResenas({ rango, ...(canal !== 'todos' ? { canal } : {}) }),
    [rango, canal],
  );

  const { grupos, visibles, catalogoZonas } = useMemo(() => {
    if (!data) return { grupos: [], visibles: 0, catalogoZonas: [] as string[] };

    const catalogo = [...data.catalogoZonas];
    if (data.listings.some(l => !l.zona) && !catalogo.includes(SIN_ZONA)) catalogo.push(SIN_ZONA);

    const q = normaliza(busqueda.trim());
    const filtrados = data.listings.filter(l => {
      const zona = l.zona ?? SIN_ZONA;
      if (zonasElegidas.length > 0 && !zonasElegidas.includes(zona)) return false;
      if (q && !normaliza(`${l.nombre} ${l.propiedad}`).includes(q)) return false;
      return true;
    });

    const mediaDeZona = new Map(data.zonas.map(z => [z.zona, z.nota5]));
    const porZona = new Map<string, FichaListing[]>();
    for (const l of filtrados) {
      const zona = l.zona ?? SIN_ZONA;
      const lista = porZona.get(zona);
      if (lista) lista.push(l);
      else porZona.set(zona, [l]);
    }

    // Sin nota va al final: una zona sin datos no es la peor, es una incógnita.
    const orden = (v: number | null) => (v === null ? Number.POSITIVE_INFINITY : v);

    const grupos = [...porZona.entries()]
      .map(([zona, listings]) => ({
        zona,
        media: mediaDeZona.get(zona) ?? null,
        // Peor primero: lo accionable arriba, porque el podio no es trabajo.
        listings: listings.slice().sort((a, b) => a.score - b.score),
      }))
      .sort((a, b) => {
        // «Sin zona» va siempre al final aunque tenga la peor media, que es lo
        // que pasa: ahí caen los listings que Hostify ya no devuelve —una o dos
        // reseñas viejas cada uno— y encabezaban la lista con un piso que la
        // empresa no gestiona desde hace años. No es una zona, es el cajón de
        // lo que no se pudo clasificar, y no puede ser lo primero que se lea.
        if ((a.zona === SIN_ZONA) !== (b.zona === SIN_ZONA)) return a.zona === SIN_ZONA ? 1 : -1;
        const ma = orden(a.media);
        const mb = orden(b.media);
        if (ma !== mb) return ma < mb ? -1 : 1;
        return a.zona.localeCompare(b.zona, 'es');
      });

    return { grupos, visibles: filtrados.length, catalogoZonas: catalogo };
  }, [data, zonasElegidas, busqueda]);

  const nFiltros = zonasElegidas.length + (canal !== 'todos' ? 1 : 0);
  const hayFiltros = nFiltros > 0 || busqueda.trim() !== '';

  function alternarZona(zona: string) {
    setZonasElegidas(prev => (prev.includes(zona) ? prev.filter(z => z !== zona) : [...prev, zona]));
  }

  function limpiarFiltros() {
    setZonasElegidas([]);
    setCanal('todos');
    setBusqueda('');
  }

  // Solo se traga la pantalla si no hay nada que enseñar. Si el error viene de
  // recargar con otro canal, la cartera anterior sigue siendo cierta y borrarla
  // deja al gestor sin nada por un fallo de red de un segundo.
  if (error && !data) {
    return <div className="card py-3 text-sm text-amber-700 dark:text-amber-300">{error}</div>;
  }

  if (!data) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="animate-spin text-slate-400" size={28} />
      </div>
    );
  }

  const { global, alerta } = data;
  const canalesGlobales = global.porCanal.filter(c => c.n > 0);
  // La zona y la búsqueda filtran en memoria, así que no vacían `listings`; el
  // canal sí, porque viaja al servidor. Por eso solo se mira ese.
  const sinDatos = data.listings.length === 0 && canal === 'todos';

  return (
    <div className="space-y-3">
      {error && <div className="card py-3 text-sm text-amber-700 dark:text-amber-300">{error}</div>}

      <FranjaAlerta alerta={alerta} />

      <div className="grid grid-cols-2 gap-3">
        <Tile
          icon={<Star size={14} />}
          label="Nota media"
          tone={
            global.nota5 === null ? 'default'
              : global.nota5 >= 4.6 ? 'good'
                : global.nota5 < 4.3 ? 'warn' : 'default'
          }
          value={
            // Sin nota se escribe una vez y con palabras: `fmt` y `Estrellas`
            // devuelven cada uno su guion y juntos dejaban un «— —» que parece
            // un fallo de pintado en vez de «todavía no hay reseñas».
            global.nota5 === null
              ? <span className="text-base">Sin nota</span>
              : (
                <span className="flex items-center gap-1.5">
                  {fmt(global.nota5, 2)}
                  <Estrellas nota={global.nota5} />
                </span>
                )
          }
          hint={
            <>
              {plural(global.nResenas, 'reseña', 'reseñas')}
              {canalesGlobales.map(c => (
                <span key={c.canal}>
                  {' · '}
                  <EtiquetaCanal
                    canal={c.canal}
                    nota5={c.nota5}
                    nativa={c.nativa}
                    escala={c.escala}
                    decimales={2}
                  />
                </span>
              ))}
            </>
          }
        />
        <Tile
          icon={<TrendingDown size={14} />}
          label="Punto débil"
          value={
            global.peorSubnota
              ? (
                <span className="text-base">
                  {global.peorSubnota.etiqueta} {fmt(global.peorSubnota.sobre5, 2)}
                </span>
                )
              : <span className="text-base">Sin sub-notas</span>
          }
          hint={
            // El signo se cuenta con palabras: «-0,34 respecto al resto» obliga a
            // parar a interpretar un menos, y aquí lo único que importa es si esa
            // sub-nota está por encima o por debajo de las demás.
            global.peorSubnota
              ? `${fmt(Math.abs(global.peorSubnota.vsMedia), 2)} ${global.peorSubnota.vsMedia < 0 ? 'por debajo' : 'por encima'} de la media del resto`
              : 'Todavía no hay sub-notas suficientes'
          }
        />
      </div>

      {esLg && <GraficoCartera serie={global.serie} />}

      {/* Barra de controles. `top-14` en móvil porque la cabecera azul de
          `Layout.tsx` va fija y mide 56 px; a partir de `lg` esa cabecera no
          existe y la barra se pega al borde. */}
      <div className="sticky top-14 lg:top-0 z-20 h-12 -mx-4 px-4 lg:-mx-6 lg:px-6 flex items-center gap-2 bg-sereno-white dark:bg-gray-900">
        <span className="text-xs text-slate-500 tabular-nums">
          {plural(visibles, 'alojamiento', 'alojamientos')}
        </span>

        <button
          type="button"
          onClick={() => setPanelAbierto(a => !a)}
          aria-expanded={panelAbierto}
          aria-controls="filtros-resenas"
          className="sm:hidden ml-auto inline-flex items-center gap-1.5 min-h-11 px-3 rounded-lg border border-slate-200 text-sm text-slate-600 dark:text-slate-300"
        >
          <SlidersHorizontal size={15} />
          {nFiltros > 0 ? `Filtros · ${nFiltros}` : 'Filtros'}
        </button>

        {/* El buscador solo desde 640 px: en el móvil, con las 65 filas ya
            partidas por zona, el filtro de zona hace de buscador. */}
        <div className="hidden sm:block ml-auto w-[200px] relative">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <input
            type="search"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder="Buscar alojamiento"
            aria-label="Buscar alojamiento"
            className="input min-h-11 pl-8"
          />
        </div>
      </div>

      {/* Panel de filtros: empuja el contenido, no flota. Los chips van en
          `flex-wrap` y nunca en una tira con desplazamiento lateral: dos ejes de
          gesto dentro de una barra pegajosa esconden la mitad de las opciones. */}
      <div
        id="filtros-resenas"
        className={classNames('card p-3 space-y-3', panelAbierto ? 'block' : 'hidden sm:block')}
      >
        {/* Sin zonas en el catálogo no se pinta ni el rótulo: un «Zona» seguido
            de nada se lee como que los chips no han cargado. */}
        {catalogoZonas.length > 0 && (
          <div>
            <div className="text-xs font-medium text-slate-500 mb-1.5">Zona</div>
            <div className="flex flex-wrap gap-2">
              {catalogoZonas.map(z => (
                <Chip key={z} activo={zonasElegidas.includes(z)} onClick={() => alternarZona(z)}>
                  {z}
                </Chip>
              ))}
            </div>
          </div>
        )}
        <div>
          <div className="text-xs font-medium text-slate-500 mb-1.5">Canal</div>
          <div className="flex flex-wrap gap-2">
            <Chip activo={canal === 'todos'} onClick={() => setCanal('todos')}>Todos</Chip>
            {CANALES_FILTRO.map(c => (
              <Chip key={c} activo={canal === c} onClick={() => setCanal(c)}>
                {ETIQUETA_CANAL[c]}
              </Chip>
            ))}
          </div>
        </div>
      </div>

      {grupos.length === 0 ? (
        <div className="card p-6 text-center text-sm text-slate-500">
          {/* Vacío por falta de datos y vacío por culpa de un filtro se leen
              igual pero se arreglan distinto: uno pide ampliar el periodo y el
              otro quitar el filtro. Decir «con los filtros elegidos» cuando no
              hay ninguno puesto manda a buscar un filtro que no existe. */}
          {sinDatos
            ? rango === 'todo'
              // Con «Todo» puesto ya no queda periodo que ampliar: decir «prueba
              // a ampliar» mandaría a buscar un control que no da más de sí.
              ? 'No hay ninguna reseña registrada todavía.'
              : 'No hay reseñas en este periodo. Prueba a ampliarlo con el selector de arriba.'
            : 'Ningún alojamiento con los filtros elegidos.'}
          {hayFiltros && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                onClick={limpiarFiltros}
                className="btn-secondary inline-flex items-center min-h-11"
              >
                Quitar los filtros
              </button>
            </div>
          )}
        </div>
      ) : (
        grupos.map(g => (
          <section key={g.zona}>
            {/* La cabecera de zona se ancla justo debajo de la barra de
                controles (56 + 48 px en móvil). Es lo que deja leer cada piso
                contra su zona y contestar «¿es el piso o es Barbate?». */}
            <div className="sticky top-[6.5rem] lg:top-12 z-10 h-9 -mx-4 px-4 lg:-mx-6 lg:px-6 flex items-center gap-1.5 border-b border-slate-100 bg-sereno-white/95 dark:bg-gray-900/95 backdrop-blur text-xs uppercase tracking-wide text-slate-500">
              <span className="truncate font-medium">{g.zona}</span>
              {g.media !== null && (
                <span className="tabular-nums whitespace-nowrap">· media {fmt(g.media, 2)}</span>
              )}
              <span className="tabular-nums whitespace-nowrap">
                · {plural(g.listings.length, 'alojamiento', 'alojamientos')}
              </span>
            </div>

            <div className="mt-2 space-y-2 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-3">
              {g.listings.map(f => <TarjetaListing key={f.listingId} f={f} />)}
            </div>
          </section>
        ))
      )}

      {/* Refresco al cambiar de rango o de canal: la lista anterior se queda en
          pantalla y solo se avisa de que hay datos en camino. */}
      {loading && (
        <div className="flex justify-center py-6">
          <Loader2 className="animate-spin text-slate-400" size={22} />
        </div>
      )}
    </div>
  );
}
